import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import * as Highcharts from 'highcharts';
import { DataService, SmartIrrigationService } from 'src/api/services';
import { DataAnalysisTimeSeriesValue, SmartIrrigationControllerDashboard, SmartIrrigationNodeDto, SmartIrrigationRootNodeDto, SmartIrrigationSoilMoistureProbe, SmartIrrigationWeatherStation } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';

export interface SensorReading {
  label: string;
  value: string | number;
  unit: string;
  status: 'red' | 'amber' | 'green';
  statusLabel: string;
}

export interface SoilSensor {
  deviceEui: string;
  siteUri?: string;
  name: string;
  subtitle: string;
  status: 'alert' | 'warning' | 'normal';
  moisture: SensorReading;
  temp: SensorReading;
  ec: SensorReading;
  alertMessage?: string;
  alertType?: 'red' | 'amber';
  lastReportedAt?: Date;
}

export interface WeatherMetric {
  label: string;
  value: string | number;
  unit: string;
  status: 'red' | 'amber' | 'green';
  statusLabel: string;
}

export interface ThresholdRow {
  parameter: string;
  green: string;
  amber: string;
  red: string;
}

@Component({
  selector: 'app-smart-irrigation-site',
  templateUrl: './site.component.html',
  styleUrls: ['./site.component.scss']
})
export class SmartIrrigationSiteComponent implements OnInit, OnDestroy {

  Highcharts = Highcharts;
  chartConstructor = 'chart';

  loading = true;
  //deviceEui: string;
  nodeId: number;
  node: SmartIrrigationRootNodeDto;
  controller: SmartIrrigationControllerDashboard;
  lastUpdated: Date | null = null;
  siteName: string;
  allNodes: SmartIrrigationNodeDto[] = [];
  /** Id of the Park node that contains the current site, used for breadcrumb navigation. */
  parkId: number | null = null;
  parkName: string | null = null;

  private routeSub: Subscription;
  sensors: SoilSensor[] = [];
  weatherMetrics: WeatherMetric[] = [];
  redCount = 0;
  amberCount = 0;
  greenCount = 0;

  // Controller activity stats
  controllerDataLoading = false;
  controllerNoData = false;
  ctrlMismatch: boolean | null = null;
  ctrlCurrentState: 'enabled' | 'disabled' | null = null;
  ctrlEvents = '--';
  ctrlDisabledDuration = '--';
  ctrlDisabledPercent = '--';
  ctrlLastDisabledAt: Date | null = null;
  ctrlLastReportedAt: Date | null = null;
  ctrlChartOptions: Highcharts.Options;
  overrideDataName = 'Irrigation Override';
  overrideDataUri = '';
  irrigationStateDataName = 'Irrigation State';
  irrigationStateDataUri = '';

  // Irrigation state data
  irrigationStateData: DataAnalysisTimeSeriesValue[] = [];
  irrigationStateNoData = false;
  ctrlIrrigationActive: boolean | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.irrigation.SmartIrrigationGetNodes(this.app.customerId).subscribe(nodes => {
      this.allNodes = (nodes || []).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    });
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.nodeId = +params.get('nodeId')!;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId),
      model: this.irrigation.SmartIrrigationGetModel(this.app.customerId),
      node: this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: this.nodeId })
    }).subscribe(({ dashboard, model, node }) => {
      this.node = node;
      this.parkId = node.parentNodeId ?? null;
      const controller = node.associatedDevices?.find(d => d.deviceTypeName === 'Controller');
      const probeNodes = (node.associatedDevices || []).filter(d => d.deviceTypeName === 'Soil Moisture');
      const ctrl = dashboard.irrigationControllers?.find(c => c.siteUri === controller?.uri);
      if (!ctrl) { this.loading = false; return; }

      this.controller = ctrl;
      this.siteName = node.name || ctrl.description || ctrl.name || ctrl.deviceEui;
      this.lastUpdated = new Date();

      const probeUris = new Set(
        probeNodes
          .map(n => n.uri)
          .filter((uri): uri is string => !!uri)
      );

      const probes = Object.values(ctrl.soilMoistureProbes || {});
        //.filter(c => !!c.siteUri && probeUris.has(c.siteUri) && c.dataUri != null);

      if (probes.length > 0) {
        this.sensors = probes
          .map(p => this.buildSoilSensor(p))
          .sort((a, b) => a.name.localeCompare(b.name));
      } 

        // need to fetch the parent node
        this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: node.parentNodeId }).subscribe(parentNode => {
          // Handle the parent node if needed
          this.parkName = parentNode.name || null;
          const ws = this.findWeatherStation(parentNode, dashboard.weatherStations || []);
          this.weatherMetrics = ws ? this.buildWeatherMetrics(ws) : [];
        });
      

      const allStatuses = [
        ...this.sensors.map(s => s.status === 'alert' ? 'red' : s.status === 'warning' ? 'amber' : 'green'),
        ...this.weatherMetrics.map(m => m.status)
      ];
      this.redCount = allStatuses.filter(s => s === 'red').length;
      this.amberCount = allStatuses.filter(s => s === 'amber').length;
      this.greenCount = allStatuses.filter(s => s === 'green').length;

      this.loading = false;
      

      const controllerUri = controller?.uri;
      this.ctrlIrrigationActive = ctrl.currentIrrigationState.value;
      this.ctrlCurrentState = ctrl.expectedState.value ? 'enabled' : 'disabled';
      this.ctrlMismatch = ctrl.expectedState.value !== ctrl.currentIrrigationState.value;
      if (controllerUri) {
        this.loadControllerData(controllerUri, ctrl.dataUri, ctrl.irrigationStateDataUri);
      } else {
        this.controllerNoData = true;
      }
    }, () => { this.loading = false; });
  }

  private findWeatherStation(
    node: SmartIrrigationRootNodeDto,
    weatherStations: SmartIrrigationWeatherStation[]
  ): SmartIrrigationWeatherStation | undefined {
    const weatherDeviceUris = (node.associatedDevices || [])
      .filter(device => (device.deviceTypeName || '').toLowerCase().includes('weather'))
      .map(device => this.normalize(device.uri))
      .filter(uri => !!uri);

    return weatherStations.find(station => {
      const stationIdentifiers = [station.deviceEui, station.siteUri]
        .map(identifier => this.normalize(identifier))
        .filter(identifier => !!identifier);
      return weatherDeviceUris.some(uri => stationIdentifiers.includes(uri));
    });
  }

  private normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
  }

  loadControllerData(siteUri: string, dataUri: string, irrigationStateUri?: string): void {
    this.controllerDataLoading = true;
    this.controllerNoData = false;
    this.irrigationStateNoData = false;
    //this.ctrlIrrigationActive = null;
    this.overrideDataUri = dataUri || 'Unavailable';
    this.irrigationStateDataUri = irrigationStateUri;

    console.log('Loading controller data for siteUri:', siteUri, 'dataUri:', dataUri, 'irrigationStateUri:', irrigationStateUri);
    ``
    // Fetch both override state and irrigation state in parallel
    forkJoin({
      overrideState: this.dataService.DataGetData({
        customerId: this.app.customerId,
        request: {
          forceEmbed: true,
          index: 0,
          period: '-14d:1h',
          type: 'TimeSeries',
          timeZoneOffset: this.app.getTimeZoneOffset(null),
          timeSeries: { single: { variable: { uri: dataUri } } },
          siteUri: siteUri,
          siteType: 'Irrigation Controller',
        }
      }),
      irrigationState: this.dataService.DataGetData({
        customerId: this.app.customerId,
        request: {
          forceEmbed: true,
          index: 0,
          period: '-14d:1h',
          type: 'TimeSeries',
          timeZoneOffset: this.app.getTimeZoneOffset(null),
          timeSeries: { single: { variable: { uri: irrigationStateUri } } },
          siteUri: siteUri,
          siteType: 'Irrigation Controller',
        }
      })
    }).subscribe(
      ({ overrideState, irrigationState }) => {
        this.overrideDataName = overrideState?.name || overrideState?.description || 'Irrigation Override';
        this.overrideDataUri = overrideState?.uri || this.overrideDataUri;
        this.irrigationStateDataName = irrigationState?.name || irrigationState?.description || 'Irrigation State';
        this.irrigationStateDataUri = irrigationState?.uri || this.irrigationStateDataUri;
        const overrideValues = overrideState?.values || [];
        const irrigationValues = irrigationState?.values || [];

        if (irrigationValues.length > 0) {
          this.irrigationStateData = irrigationValues;
        }

        if (overrideValues.length > 0) {
          this.computeControllerStats(overrideValues);
          if (irrigationValues.length > 0) {
            this.buildControllerChart(overrideValues, irrigationValues);
          } else {
            this.irrigationStateNoData = true;
            this.buildControllerChart(overrideValues, []);
          }
        } else {
          this.controllerNoData = true;
        }
        this.controllerDataLoading = false;
      },
      () => {
        this.controllerNoData = true;
        this.irrigationStateNoData = true;
        this.controllerDataLoading = false;
      }
    );
  }

  private computeControllerStats(values: DataAnalysisTimeSeriesValue[]): void {
    const sorted = [...values].sort((a, b) =>
      new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
    );

    // Last time the controller reported in
    this.ctrlLastReportedAt = sorted.length > 0 ? new Date(sorted[sorted.length - 1].timeStamp) : null;

    // Count 0→1 transitions (control events = irrigation was enabled)
    let events = 0;
    for (let i = 1; i < sorted.length; i++) {
      if ((sorted[i - 1].value ?? 0) < 0.5 && (sorted[i].value ?? 0) >= 0.5) {
        events++;
      }
    }
    this.ctrlEvents = events.toString();

    // Last time irrigation was disabled (last 1→0 transition)
    this.ctrlLastDisabledAt = null;
    for (let i = sorted.length - 1; i >= 1; i--) {
      if ((sorted[i - 1].value ?? 0) >= 0.5 && (sorted[i].value ?? 0) < 0.5) {
        this.ctrlLastDisabledAt = new Date(sorted[i].timeStamp);
        break;
      }
    }

    // Total time irrigation was disabled (value < 0.5)
    let disabledMs = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      if ((sorted[i].value ?? 0) < 0.5) {
        const dt = new Date(sorted[i + 1].timeStamp).getTime() - new Date(sorted[i].timeStamp).getTime();
        disabledMs += dt;
      }
    }
    const totalHours = Math.floor(disabledMs / (3600 * 1000));
    const remainMins = Math.floor((disabledMs % (3600 * 1000)) / 60000);
    this.ctrlDisabledDuration = totalHours > 0
      ? `${totalHours}h ${remainMins}m`
      : `${remainMins}m`;

    // % of window that irrigation was disabled
    const windowMs = sorted.length > 1
      ? new Date(sorted[sorted.length - 1].timeStamp).getTime() - new Date(sorted[0].timeStamp).getTime()
      : 0;
    this.ctrlDisabledPercent = windowMs > 0
      ? `${Math.round(disabledMs / windowMs * 100)}%`
      : '--';
  }

  private buildControllerChart(overrideValues: DataAnalysisTimeSeriesValue[], irrigationStateValues: DataAnalysisTimeSeriesValue[] = []): void {
    const sortedOverride = [...overrideValues].sort((a, b) =>
      new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
    );
    const overrideData: [number, number][] = sortedOverride
      .filter(v => v.value != null)
      .map(v => [new Date(v.timeStamp).getTime(), v.value]);

    const sortedIrrigation = [...irrigationStateValues].sort((a, b) =>
      new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
    );
    const irrigationData: [number, number][] = sortedIrrigation
      .filter(v => v.value != null)
      .map(v => [new Date(v.timeStamp).getTime(), v.value]);

    // Build plot bands for override state (green for enabled, red for disabled)
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];
    if (overrideData.length > 0) {
      let segStart = overrideData[0][0];
      let segEnabled = overrideData[0][1] >= 0.5;
      for (let i = 1; i < overrideData.length; i++) {
        const nextEnabled = overrideData[i][1] >= 0.5;
        if (nextEnabled !== segEnabled) {
          plotBands.push({
            from: segStart,
            to: overrideData[i][0],
            color: segEnabled ? 'rgba(56,161,105,0.3)' : 'rgba(229,62,62,0.3)'
          });
          segStart = overrideData[i][0];
          segEnabled = nextEnabled;
        }
      }
      plotBands.push({
        from: segStart,
        to: overrideData[overrideData.length - 1][0],
        color: segEnabled ? 'rgba(56,161,105,0.3)' : 'rgba(229,62,62,0.3)'
      });
    }

    const ms24h = 24 * 3600 * 1000;
    const now = Date.now();
    const maxX = this.getMidnightTonightMs();
    const minX = maxX - 14 * ms24h;

    // Build series array
    const series: Highcharts.SeriesOptionsType[] = [{
      name: 'Irrigation Override',
      type: 'line',
      data: overrideData,
      color: 'rgba(255,255,255,0.4)'
    } as any];

    if (irrigationData.length > 0) {
      series.push({
        name: 'Irrigation State',
        type: 'line',
        data: irrigationData,
        color: '#0d6efd',
        step: 'left'
      } as any);
    }

    this.ctrlChartOptions = {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        spacing: [10, 10, 10, 10]
      },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: minX,
        max: maxX,
        tickInterval: ms24h,
        gridLineWidth: 1,
        plotLines: [{
          value: now,
          color: '#6c757d',
          dashStyle: 'ShortDash',
          width: 2,
          zIndex: 10,
          label: {
            text: 'Now',
            rotation: 0,
            align: 'left',
            x: 4,
            y: -8,
            style: { color: '#6c757d', fontSize: '10px' }
          }
        }],
        labels: { format: '{value:%d %b}', rotation: -30 },
        plotBands
      },
      yAxis: {
        min: 0,
        max: 1,
        tickPositions: [0, 1],
        title: { text: null },
        labels: {
          formatter() {
            return (this.value as number) >= 0.5 ? 'On' : 'Off';
          }
        }
      },
      tooltip: {
        xDateFormat: '%d %b %Y %H:%M',
        formatter() {
          const seriesName = this.series.name;
          const state = (this.y ?? 0) >= 0.5 ? 'On' : 'Off';
          return `<b>${Highcharts.dateFormat('%d %b %Y %H:%M', this.x as number)}</b><br/>${seriesName}: <b>${state}</b>`;
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: {
        line: {
          step: 'left',
          lineWidth: 2,
          marker: { enabled: false }
        }
      },
      series
    };
  }

  private getMidnightTonightMs(): number {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  private buildSoilSensor(probe: SmartIrrigationSoilMoistureProbe): SoilSensor {
    const val = probe.currentSoilMoisture?.value;
    const mStatus = this.moistureStatus(val);
    const sensorStatus: SoilSensor['status'] = mStatus === 'red' ? 'alert' : mStatus === 'amber' ? 'warning' : 'normal';
    return {
      deviceEui: probe.deviceEui,
      siteUri: probe.siteUri,
      name: probe.description || probe.name || probe.deviceEui,
      subtitle: 'Moisture',
      status: sensorStatus,
      moisture: this.moistureReading(val),
      temp: this.temperatureReading(probe.currentSoilTemperature?.value),
      ec: this.conductivityReading(probe.currentSoilConductivity?.value),
      alertMessage: sensorStatus !== 'normal' ? `Soil moisture at ${val != null ? val.toFixed(1) + '%' : 'N/A'}` : undefined,
      alertType: mStatus === 'red' ? 'red' : 'amber',
      lastReportedAt: probe.currentSoilMoisture?.timeStamp ? new Date(probe.currentSoilMoisture.timeStamp) : undefined
    };
  }

  private buildControllerSensor(ctrl: SmartIrrigationControllerDashboard): SoilSensor {
    const val = ctrl.soilMoisturePercentage?.value;
    const mStatus = this.moistureStatus(val);
    const sensorStatus: SoilSensor['status'] = mStatus === 'red' ? 'alert' : mStatus === 'amber' ? 'warning' : 'normal';
    return {
      deviceEui: ctrl.deviceEui,
      name: ctrl.description || ctrl.name || ctrl.deviceEui,
      subtitle: 'Moisture',
      status: sensorStatus,
      moisture: this.moistureReading(val),
      temp: this.naReading('Temp', '°C'),
      ec: this.naReading('EC', 'mS/cm'),
      alertMessage: sensorStatus !== 'normal' ? `Soil moisture at ${val != null ? val.toFixed(1) + '%' : 'N/A'}` : undefined,
      alertType: mStatus === 'red' ? 'red' : 'amber'
    };
  }

  private moistureReading(val: number): SensorReading {
    const status = this.moistureStatus(val);
    return {
      label: 'Moisture',
      value: val != null ? val.toFixed(1) : 'N/A',
      unit: '% VMC',
      status,
      statusLabel: status === 'red' ? 'Out of range' : status === 'amber' ? 'Watch trend' : 'Normal'
    };
  }

  private moistureStatus(val: number): 'red' | 'amber' | 'green' {
    if (val == null) return 'green';
    if (val < 15 || val > 60) return 'red';
    if (val < 20 || val > 50) return 'amber';
    return 'green';
  }

  private naReading(label: string, unit: string): SensorReading {
    return { label, value: 'N/A', unit, status: 'green', statusLabel: 'No data' };
  }

  private temperatureReading(val: number): SensorReading {
    const status: SensorReading['status'] = val == null
      ? 'green'
      : val < 5 || val > 40
        ? 'red'
        : val < 10 || val > 35
          ? 'amber'
          : 'green';
    return this.parameterReading('Temp', val, '°C', status,
      status === 'red' ? 'Out of range' : status === 'amber' ? 'Watch trend' : 'Measured');
  }

  private conductivityReading(val: number): SensorReading {
    const status: SensorReading['status'] = val == null
      ? 'green'
      : val > 4000
        ? 'red'
        : val > 2000
          ? 'amber'
          : 'green';
    return this.parameterReading('EC', val, 'µS/cm', status,
      status === 'red' ? 'High salinity' : status === 'amber' ? 'Elevated salinity' : 'Measured');
  }

  private parameterReading(label: string, val: number, unit: string, status: SensorReading['status'], statusLabel: string): SensorReading {
    return val != null
      ? { label, value: val.toFixed(1), unit, status, statusLabel }
      : this.naReading(label, unit);
  }

  private buildWeatherMetrics(ws: SmartIrrigationWeatherStation): WeatherMetric[] {
    return [
      this.windMetric(ws.currentWindSpeed?.value),
      this.rainfallMetric(ws.currentRainfall?.value),
      this.tempMetric(ws.currentTemperature?.value),
      this.humidityMetric(ws.currentHumidity?.value)
    ];
  }

  private windMetric(val: number): WeatherMetric {
    const status: WeatherMetric['status'] = val == null ? 'green' : val > 17 ? 'red' : val > 11 ? 'amber' : 'green';
    return { label: 'Wind Speed', value: val != null ? val.toFixed(1) : 'N/A', unit: 'm/s', status, statusLabel: status === 'red' ? 'High' : status === 'amber' ? 'Elevated' : 'Normal' };
  }

  private rainfallMetric(val: number): WeatherMetric {
    const status: WeatherMetric['status'] = val == null ? 'green' : val > 50 ? 'red' : val > 20 ? 'amber' : 'green';
    return { label: 'Rainfall (24h)', value: val != null ? val.toFixed(1) : 'N/A', unit: 'mm', status, statusLabel: status === 'red' ? 'Heavy' : status === 'amber' ? 'Elevated' : 'Normal' };
  }

  private tempMetric(val: number): WeatherMetric {
    return { label: 'Temperature', value: val != null ? val.toFixed(1) : 'N/A', unit: '°C', status: 'green', statusLabel: 'Measured' };
  }

  private humidityMetric(val: number): WeatherMetric {
    return { label: 'Humidity', value: val != null ? val.toFixed(0) : 'N/A', unit: '%', status: 'green', statusLabel: 'Measured' };
  }

  viewSensorTrend(sensor: SoilSensor): void {
    this.router.navigate(['/smart-irrigation/sensor-trend/' + this.node.id], {
      queryParams: {
        siteUri: sensor.siteUri,
        controllerDeviceEui: this.controller.deviceEui,
        name: sensor.name
      }
    });
  }

  viewWeatherTrend(): void {
    this.router.navigate(['/smart-irrigation/weather-trend/' + this.node.parentNodeId]);
  }

  navigateToSite(nodeId: number): void {
    this.router.navigate(['/smart-irrigation/site', nodeId]);
  }

  goBack(): void {
    this.router.navigate(['/smart-irrigation/regions']);
  }
}
